import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Grid,
  IconButton,
  Input,
  List,
  ListItem,
  Paper,
  Rating,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Storefront as StorefrontIcon } from "../../icons";
import { Link as LinkIcon } from "../../icons";
import { Edit as EditIcon } from "../../icons";
import { AddPhotoAlternate as AddPhotoAlternateIcon } from "../../icons";
import { VideoCall as VideoCallIcon } from "../../icons";
import { Close as CloseIcon } from "../../icons";
import { PlayCircle as PlayCircleIcon } from "../../icons";
import { Delete as DeleteIcon } from "../../icons";
import { Search as SearchIcon } from "../../icons";
import { ExpandMore as ExpandMoreIcon } from "../../icons";
import { ExpandLess as ExpandLessIcon } from "../../icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { brandColors } from "../../theme";
import firebase from "firebase/compat/app";
import { auth, db, uploadImage, uploadVideo } from "../../firebase";
import { useSubscriptionContext } from "../SubscriptionContext";
import StoresAppBar from "./StoresAppBar";
import StoreDashboardTab from "./StoreDashboardTab";
import StoreAppearanceForm from "./StoreAppearanceForm";
import GoogleAdSideRail from "../../components/GoogleAdSideRail";
import GoogleAdInline from "../../components/GoogleAdInline";
import GuestJoinBanner from "../../components/GuestJoinBanner";
import { T } from "../Translate";
import {
  postStoreQuestion,
  postStoreAnswer,
  fetchStoreQuestionsAndAnswers,
  submitStoreRating,
  fetchStoreRatings,
  getPendingCommission,
} from "./storesUtils";
import { pageContentSx } from "../../pageLayout";

import SearchClearAdornment from "../../components/SearchClearAdornment";
const PRODUCT_LIMITS = {
  Free: 10,
  Básico: 10,
  Profesional: 50,
  Premium: Infinity,
};

const formatFromSlug = (slug = "") =>
  slug
    .split("-")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ") || "Tu tienda";


const formatPrice = (value) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);

const OWNER_TABS = [
  { value: "tienda", label: <T>Tienda</T> },
  { value: "productos", label: <T>Productos</T> },
  { value: "panel", label: <T>Panel de control</T> },
  { value: "apariencia", label: <T>Apariencia y datos</T> },
];

const StoreDetail = () => {
  const { storeSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(
    OWNER_TABS.some((t) => t.value === location.state?.tab) ? location.state.tab : "tienda"
  );

  const [storeData, setStoreData] = useState({
    storeName: location.state?.storeName || formatFromSlug(storeSlug),
    description:
      location.state?.description || <T>Agrega una descripción desde el asistente de tiendas.</T>,
    ownerId: "",
    ownerEmail: "",
    createdAt: null,
    updatedAt: null,
    paypalEmail: location.state?.paypalEmail || "",
    paypalClientId: location.state?.paypalClientId || "",
    address: "",
    hours: "",
    contactEmail: "",
    whatsapp: "",
    logoUrl: "",
    bannerUrl: "",
    accentColor: "",
    avgRating: 0,
    ratingCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [storeLoaded, setStoreLoaded] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productForm, setProductForm] = useState({
    name: "",
    price: "",
    description: "",
    couponCode: "",
    couponDiscount: "",
    stock: "",
  });
  const MAX_PRODUCT_IMAGES = 6;
  const [productImages, setProductImages] = useState([]); // File[] nuevas, pendientes de subir
  const [existingImages, setExistingImages] = useState([]); // string[] ya subidas (al editar)
  const [productVideo, setProductVideo] = useState(null); // File nuevo, pendiente de subir
  const [existingVideoUrl, setExistingVideoUrl] = useState(""); // string ya subido (al editar)
  const [removeExistingVideo, setRemoveExistingVideo] = useState(false);
  const [imageInputKey, setImageInputKey] = useState(() => Date.now());
  const [videoInputKey, setVideoInputKey] = useState(() => Date.now());
  // Miniaturas locales de las imágenes nuevas todavía no subidas; se liberan
  // al desmontar o al cambiar la selección para no acumular blob URLs.
  const productImagePreviews = React.useMemo(
    () => productImages.map((file) => URL.createObjectURL(file)),
    [productImages]
  );
  useEffect(() => {
    return () => { productImagePreviews.forEach((url) => URL.revokeObjectURL(url)); };
  }, [productImagePreviews]);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productFeedback, setProductFeedback] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingProductId, setEditingProductId] = useState(null);
  const productFormRef = React.useRef(null);

  const [ratings, setRatings] = useState([]);
  const [myRatingValue, setMyRatingValue] = useState(0);
  const [myRatingComment, setMyRatingComment] = useState("");
  const [submittingRating, setSubmittingRating] = useState(false);
  const [ratingFeedback, setRatingFeedback] = useState(null);

  const [questionsAndAnswers, setQuestionsAndAnswers] = useState([]);
  const [qaOpen, setQaOpen] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState({});
  const [showMoreQuestions, setShowMoreQuestions] = useState(false);

  // Keep filteredProducts in sync when products load
  useEffect(() => { setFilteredProducts(products); setSearchTerm(""); }, [products]);

  const handleProductSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredProducts(products);
      return;
    }
    const normalized = (t) => t?.toString().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "") ?? "";
    const q = normalized(term);
    setFilteredProducts(products.filter((p) =>
      normalized(p.name).includes(q) || normalized(p.description).includes(q)
    ));
  };
  const { subscription, isSubscriptionActive } = useSubscriptionContext();
  const productLimit = PRODUCT_LIMITS[subscription?.planName] ?? 0;
  const canAddProduct = isSubscriptionActive && products.length < productLimit;
  const productLimitMessage = !isSubscriptionActive
    ? <T>Necesitas una suscripción activa para agregar productos.</T>
    : products.length >= productLimit
    ? <T>Tu plan {subscription?.planName} permite hasta {productLimit} producto{productLimit !== 1 ? "s" : ""} por tienda. Mejora tu plan para agregar más.</T>
    : "";

  useEffect(() => {
    let active = true;

    const fetchStore = async () => {
      setLoading(true);
      try {
        const snapshot = await db.collection("stores").doc(storeSlug).get();

        if (!snapshot.exists) {
          if (active) {
            setError(<T>No encontramos esta tienda. Revisa la URL o crea una nueva.</T>);
            setStoreLoaded(false);
            setLoadingProducts(false);
          }
          return;
        }

        const data = snapshot.data();
        if (active) {
          setStoreData({
            storeName: data.storeName || formatFromSlug(storeSlug),
            description:
              data.description || <T>Agrega una descripción desde el asistente de tiendas.</T>,
            ownerId: data.ownerId || "",
            ownerEmail: data.ownerEmail || "",
            createdAt: data.createdAt?.toDate() ?? null,
            updatedAt: data.updatedAt?.toDate() ?? null,
            paypalEmail: data.paypalEmail || "",
            paypalClientId: data.paypalClientId || "",
            address: data.address || "",
            hours: data.hours || "",
            contactEmail: data.contactEmail || "",
            whatsapp: data.whatsapp || "",
            logoUrl: data.logoUrl || "",
            bannerUrl: data.bannerUrl || "",
            accentColor: data.accentColor || "",
            latitude: data.latitude ?? null,
            longitude: data.longitude ?? null,
            avgRating: data.avgRating || 0,
            ratingCount: data.ratingCount || 0,
          });
          setStoreLoaded(true);
          setError(null);
        }
      } catch (err) {
        console.error("Error al cargar la tienda:", err);
        if (active) {
          setError(<T>Ocurrió un error al cargar la tienda.</T>);
          setStoreLoaded(false);
          setLoadingProducts(false);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchStore();

    return () => {
      active = false;
    };
  }, [storeSlug]);

  useEffect(() => {
    if (!storeLoaded) {
      return;
    }

    setLoadingProducts(true);
    const unsubscribe = db
      .collection("stores")
      .doc(storeSlug)
      .collection("products")
      .orderBy("createdAt", "desc")
      .onSnapshot(
        (snapshot) => {
          const items = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          setProducts(items);
          setLoadingProducts(false);
        },
        (err) => {
          console.error("Error al cargar productos:", err);
          setProductFeedback({ type: "error", message: <T>No pudimos cargar los productos.</T> });
          setLoadingProducts(false);
        }
      );

    return () => unsubscribe();
  }, [storeLoaded, storeSlug]);

  useEffect(() => {
    if (!storeLoaded) {
      return;
    }

    const unsubscribe = db.collection("stores").doc(storeSlug).onSnapshot((snapshot) => {
      if (!snapshot.exists) return;
      const data = snapshot.data();
      setStoreData((prev) => ({
        ...prev,
        avgRating: data.avgRating || 0,
        ratingCount: data.ratingCount || 0,
      }));
    });

    return () => unsubscribe();
  }, [storeLoaded, storeSlug]);

  useEffect(() => {
    if (!storeLoaded) {
      return;
    }

    const unsubscribeRatings = fetchStoreRatings(storeSlug, setRatings);
    const unsubscribeQA = fetchStoreQuestionsAndAnswers(storeSlug, setQuestionsAndAnswers);

    return () => {
      unsubscribeRatings();
      unsubscribeQA();
    };
  }, [storeLoaded, storeSlug]);

  const currentUserId = auth.currentUser?.uid || "";
  const isOwner = Boolean(storeData.ownerId && currentUserId === storeData.ownerId);
  const isGuest = !currentUserId;
  const accentColor = storeData.accentColor || brandColors.accent;

  const [pendingCommission, setPendingCommission] = useState({ amount: 0, salesCount: 0 });

  const refreshPendingCommission = React.useCallback(() => {
    if (!isOwner || !currentUserId) return;
    getPendingCommission(storeSlug, currentUserId).then(setPendingCommission);
  }, [isOwner, currentUserId, storeSlug]);

  useEffect(() => {
    refreshPendingCommission();
  }, [refreshPendingCommission]);

  useEffect(() => {
    if (!currentUserId) {
      setMyRatingValue(0);
      setMyRatingComment("");
      return;
    }
    const mine = ratings.find((r) => r.userId === currentUserId);
    if (mine) {
      setMyRatingValue(mine.rating || 0);
      setMyRatingComment(mine.comment || "");
    }
  }, [ratings, currentUserId]);

  const handleSubmitRating = async () => {
    if (!currentUserId) {
      setRatingFeedback({ type: "error", message: <T>Debes iniciar sesión para calificar esta tienda.</T> });
      return;
    }
    if (!myRatingValue) {
      setRatingFeedback({ type: "error", message: <T>Selecciona una cantidad de estrellas.</T> });
      return;
    }

    setSubmittingRating(true);
    setRatingFeedback(null);
    try {
      await submitStoreRating({
        storeSlug,
        userId: currentUserId,
        displayName: auth.currentUser?.displayName || auth.currentUser?.email || "Usuario",
        profileImageUrl: auth.currentUser?.photoURL || "",
        rating: myRatingValue,
        comment: myRatingComment.trim(),
      });
      setRatingFeedback({ type: "success", message: <T>¡Gracias por tu calificación!</T> });
    } catch (err) {
      console.error("Error al calificar la tienda:", err);
      setRatingFeedback({ type: "error", message: <T>No pudimos guardar tu calificación.</T> });
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleQuestionSubmit = async () => {
    if (!currentUserId || !newQuestion.trim()) return;
    await postStoreQuestion(storeSlug, currentUserId, newQuestion.trim());
    setNewQuestion("");
  };

  const handleAnswerSubmit = async (questionId) => {
    const answerText = newAnswer[questionId]?.trim();
    if (!currentUserId || !answerText) return;
    await postStoreAnswer(questionId, currentUserId, answerText, storeSlug);
    setNewAnswer((prev) => ({ ...prev, [questionId]: "" }));
  };


  const handleProductInputChange = (event) => {
    const { name, value } = event.target;
    setProductForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleProductImageChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setProductImages((prev) => {
      const room = MAX_PRODUCT_IMAGES - existingImages.length - prev.length;
      if (room <= 0) {
        setProductFeedback({ type: "warning", message: <T>Ya llegaste al máximo de {MAX_PRODUCT_IMAGES} imágenes por producto.</T> });
        return prev;
      }
      return [...prev, ...files.slice(0, room)];
    });
    setImageInputKey(Date.now());
  };

  const handleRemoveNewImage = (index) => {
    setProductImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleProductVideoChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (file && file.size > 100 * 1024 * 1024) {
      setProductFeedback({ type: "error", message: <T>El video no puede superar los 100MB.</T> });
      setVideoInputKey(Date.now());
      return;
    }
    setProductVideo(file);
    setRemoveExistingVideo(false);
  };

  const handleRemoveVideo = () => {
    setProductVideo(null);
    setRemoveExistingVideo(true);
    setVideoInputKey(Date.now());
  };

  const resetProductForm = () => {
    setProductForm({ name: "", price: "", description: "", couponCode: "", couponDiscount: "", stock: "" });
    setProductImages([]);
    setExistingImages([]);
    setProductVideo(null);
    setExistingVideoUrl("");
    setRemoveExistingVideo(false);
    setImageInputKey(Date.now());
    setVideoInputKey(Date.now());
    setEditingProductId(null);
  };

  const handleEditProductClick = (product) => {
    setActiveTab("productos");
    setEditingProductId(product.id);
    setExistingImages(product.images?.length ? product.images : (product.imageUrl ? [product.imageUrl] : []));
    setExistingVideoUrl(product.videoUrl || "");
    setRemoveExistingVideo(false);
    setProductForm({
      name: product.name || "",
      price: product.price != null ? String(product.price) : "",
      description: product.description || "",
      couponCode: product.couponCode || "",
      couponDiscount: product.couponDiscount != null ? String(product.couponDiscount) : "",
      stock: product.stock != null ? String(product.stock) : "",
    });
    setProductImages([]);
    setProductVideo(null);
    setImageInputKey(Date.now());
    setVideoInputKey(Date.now());
    setProductFeedback(null);
  };

  const handleSubmitProduct = async (event) => {
    event.preventDefault();

    const isEditing = Boolean(editingProductId);

    if (!isOwner) {
      setProductFeedback({
        type: "error",
        message: isEditing
          ? <T>Solo el administrador de la tienda puede editar productos.</T>
          : <T>Solo el administrador de la tienda puede agregar productos.</T>,
      });
      return;
    }

    if (!isEditing && !canAddProduct) {
      setProductFeedback({ type: "warning", message: productLimitMessage });
      return;
    }

    if (!isEditing && pendingCommission.amount > 0) {
      setProductFeedback({
        type: "warning",
        message: <T>Tenés comisión pendiente de pago. Pagala desde "Panel de control" para poder cargar productos nuevos.</T>,
      });
      return;
    }

    const cleanName = productForm.name.trim();
    const cleanDescription = productForm.description.trim();
    const numericPrice = Number(productForm.price);
    const cleanCouponCode = productForm.couponCode.trim().toUpperCase();
    const numericCouponDiscount = Number(productForm.couponDiscount);
    const cleanStock = productForm.stock.trim();
    const numericStock = cleanStock === "" ? null : Number(cleanStock);

    if (!cleanName || !cleanDescription || Number.isNaN(numericPrice) || numericPrice <= 0) {
      setProductFeedback({
        type: "error",
        message: <T>Completa nombre, descripción y un precio válido mayor a cero.</T>,
      });
      return;
    }

    if (cleanCouponCode && (Number.isNaN(numericCouponDiscount) || numericCouponDiscount <= 0 || numericCouponDiscount > 90)) {
      setProductFeedback({
        type: "error",
        message: <T>Ingresa un descuento de cupón válido entre 1% y 90%.</T>,
      });
      return;
    }

    if (cleanStock !== "" && (Number.isNaN(numericStock) || !Number.isInteger(numericStock) || numericStock < 0)) {
      setProductFeedback({
        type: "error",
        message: <T>Ingresa una cantidad en stock válida (número entero, 0 o mayor).</T>,
      });
      return;
    }

    setSavingProduct(true);
    setProductFeedback(null);

    try {
      const uploadedImageUrls = await Promise.all(productImages.map((file) => uploadImage(file)));
      const images = [...existingImages, ...uploadedImageUrls];

      let videoUrl = isEditing && !removeExistingVideo ? existingVideoUrl : "";
      if (productVideo) {
        videoUrl = await uploadVideo(productVideo);
      }

      const payload = {
        name: cleanName,
        description: cleanDescription,
        price: numericPrice,
        images,
        imageUrl: images[0] || "", // se conserva para pantallas que todavía leen el campo singular
        videoUrl: videoUrl || null,
        couponCode: cleanCouponCode || null,
        couponDiscount: cleanCouponCode ? numericCouponDiscount : null,
        stock: numericStock,
      };

      const productsRef = db.collection("stores").doc(storeSlug).collection("products");

      if (isEditing) {
        payload.updatedAt = firebase.firestore.FieldValue.serverTimestamp();
        await productsRef.doc(editingProductId).update(payload);
      } else {
        payload.createdAt = firebase.firestore.FieldValue.serverTimestamp();
        payload.ownerId = currentUserId;
        await productsRef.add(payload);
      }

      resetProductForm();
      setProductFeedback({
        type: "success",
        message: isEditing ? <T>Producto actualizado correctamente.</T> : <T>Producto agregado correctamente.</T>,
      });
    } catch (err) {
      console.error("Error al guardar producto:", err);
      setProductFeedback({ type: "error", message: <T>No pudimos guardar el producto.</T> });
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!isOwner) {
      setProductFeedback({ type: "error", message: <T>Solo el administrador puede eliminar productos.</T> });
      return;
    }

    try {
      await db
        .collection("stores")
        .doc(storeSlug)
        .collection("products")
        .doc(productId)
        .delete();
      setProductFeedback({ type: "success", message: <T>Producto eliminado.</T> });
    } catch (err) {
      console.error("Error al eliminar producto:", err);
      setProductFeedback({ type: "error", message: <T>No pudimos eliminar el producto.</T> });
    }
  };

  const handleAppearanceSaved = (updates) => {
    setStoreData((prev) => ({ ...prev, ...updates }));
  };

  const publicUrl = `notiline.com/stores/${storeSlug}`;

  const storeLabel = isOwner ? <T>Tu tienda en Notiline</T> : <T>Tienda en Notiline</T>;

  const showTienda = !isOwner || activeTab === "tienda";
  const showProductos = isOwner && activeTab === "productos";
  const showPanel = isOwner && activeTab === "panel";
  const showApariencia = isOwner && activeTab === "apariencia";

  return (
    <Box sx={{ bgcolor: brandColors.backgroundDefault, minHeight: "100vh", pb: "80px" }}>
      <StoresAppBar
        title={storeData.storeName}
        subtitle={storeLabel}
        quickNav={!isGuest}
        maxWidth="md"
      />
      <Container maxWidth={false} disableGutters sx={{ ...pageContentSx, pt: { xs: 6, md: 10 }, position: "relative" }}>
        {/* AdSense: solo en la vista pública de una tienda ya cargada (no durante
            la carga, si no existe, ni en las pestañas de gestión del dueño) */}
        {!loading && storeLoaded && !error && showTienda && <GoogleAdSideRail />}
        {isGuest && <GuestJoinBanner />}
        <Paper sx={{ p: { xs: 2.5, sm: 4, md: 6 }, borderRadius: 4, backgroundColor: brandColors.backgroundPaper, border: `1px solid ${brandColors.divider}` }}>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2, color: "text.secondary" }}>
                <T>Cargando tienda...</T>
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={3}>
              {error && <Alert severity="warning">{error}</Alert>}
              {productFeedback && (
                <Alert
                  severity={productFeedback.type}
                  onClose={() => setProductFeedback(null)}
                >
                  {productFeedback.message}
                </Alert>
              )}

              {isOwner && (
                <Tabs
                  value={activeTab}
                  onChange={(e, value) => setActiveTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                    minHeight: 44,
                    "& .MuiTab-root": {
                      minHeight: 44,
                      minWidth: "auto",
                      px: { xs: 1.5, sm: 2 },
                      fontSize: { xs: "0.8rem", sm: "0.875rem" },
                      whiteSpace: "nowrap",
                    },
                    "& .MuiTabs-scrollButtons": {
                      color: accentColor,
                      width: 32,
                      "&.Mui-disabled": { opacity: 0.2 },
                    },
                  }}
                >
                  {OWNER_TABS.map((tab) => (
                    <Tab key={tab.value} value={tab.value} label={tab.label} />
                  ))}
                </Tabs>
              )}

              {showTienda && (
                <Stack spacing={3}>
                  {storeData.bannerUrl && (
                    <Box
                      component="img"
                      src={storeData.bannerUrl}
                      alt=""
                      sx={{ width: "100%", height: { xs: 140, sm: 200 }, objectFit: "cover", borderRadius: 3 }}
                    />
                  )}
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      src={storeData.logoUrl || undefined}
                      sx={{ width: { xs: 56, sm: 72 }, height: { xs: 56, sm: 72 }, bgcolor: brandColors.primary }}
                    >
                      <StorefrontIcon sx={{ color: accentColor, fontSize: { xs: 28, sm: 36 } }} />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="overline" sx={{ color: "text.secondary" }}>
                        {storeLabel}
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{ fontWeight: 700, fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" }, wordBreak: "break-word" }}
                      >
                        {storeData.storeName}
                      </Typography>
                    </Box>
                  </Stack>

                  <Typography variant="body1" sx={{ color: "text.secondary" }}>
                    {storeData.description}
                  </Typography>

                  <Stack spacing={1}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      <T>URL pública</T>
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LinkIcon color="action" />
                      <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                        {publicUrl}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Box>
                    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="center" mb={2} gap={2}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        <T>Productos publicados</T>
                      </Typography>
                      {!loadingProducts && products.length > 0 && (
                        <TextField
                          size="small"
                          placeholder="Buscar producto..."
                          value={searchTerm}
                          onChange={handleProductSearch}
                          InputProps={{ endAdornment: <SearchClearAdornment value={searchTerm} onChange={handleProductSearch} />, startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} /> }}
                          sx={{ minWidth: 220 }}
                        />
                      )}
                      {loadingProducts && (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          <T>Actualizando lista...</T>
                        </Typography>
                      )}
                    </Stack>

                    {loadingProducts ? (
                      <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          <T>Cargando productos</T>
                        </Typography>
                      </Stack>
                    ) : products.length === 0 ? (
                      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, backgroundColor: brandColors.backgroundPaper, borderColor: "divider" }}>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          <T>Aún no hay productos publicados para esta tienda.</T>
                        </Typography>
                      </Paper>
                    ) : (
                      <Grid container spacing={3}>
                        {filteredProducts.map((product) => (
                          <Grid item xs={12} md={6} key={product.id}>
                            <Card sx={{ height: "100%", display: "flex", flexDirection: "column", backgroundColor: brandColors.backgroundPaper, border: "1px solid rgba(255,255,255,0.1)" }}>
                              {product.imageUrl && (
                                <CardMedia
                                  component="img"
                                  height="180"
                                  image={product.imageUrl}
                                  alt={product.name}
                                  sx={{ objectFit: "cover" }}
                                />
                              )}
                              <CardContent sx={{ flexGrow: 1 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} gap={1}>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      minWidth: 0,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {product.name}
                                  </Typography>
                                  {isOwner && (
                                    <IconButton
                                      aria-label="Eliminar producto"
                                      onClick={() => handleDeleteProduct(product.id)}
                                      size="small"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Stack>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: accentColor }}>
                                  {formatPrice(product.price)}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                                  {product.description}
                                </Typography>
                                {product.couponCode && (
                                  <Chip
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    sx={{ mt: 1 }}
                                    label={`${product.couponCode} · -${product.couponDiscount}%`}
                                  />
                                )}
                                {product.stock != null && (
                                  <Chip
                                    size="small"
                                    color={product.stock > 0 ? "default" : "error"}
                                    variant="outlined"
                                    sx={{ mt: 1, ml: product.couponCode ? 1 : 0 }}
                                    label={product.stock > 0 ? <T>Stock: {product.stock}</T> : <T>Agotado</T>}
                                  />
                                )}
                                <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} flexWrap="wrap" rowGap={1}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    sx={{ color: accentColor, borderColor: accentColor }}
                                    onClick={() =>
                                      navigate(`/stores/${storeSlug}/products/${product.id}`, {
                                        state: {
                                          storeName: storeData.storeName,
                                          productName: product.name,
                                          price: product.price,
                                          imageUrl: product.imageUrl || "",
                                          images: product.images?.length ? product.images : (product.imageUrl ? [product.imageUrl] : []),
                                          videoUrl: product.videoUrl || null,
                                          description: product.description,
                                        },
                                      })
                                    }
                                  >
                                    <T>Ver producto</T>
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    sx={{ bgcolor: accentColor, color: "#111", "&:hover": { bgcolor: accentColor, opacity: 0.9 } }}
                                    onClick={() =>
                                      navigate(`/stores/${storeSlug}/products/${product.id}/payment`, {
                                        state: {
                                          storeName: storeData.storeName,
                                          productName: product.name,
                                          price: product.price,
                                          imageUrl: product.imageUrl || "",
                                          images: product.images?.length ? product.images : (product.imageUrl ? [product.imageUrl] : []),
                                          videoUrl: product.videoUrl || null,
                                          description: product.description,
                                        },
                                      })
                                    }
                                  >
                                    <T>Comprar</T>
                                  </Button>
                                  {isOwner && (
                                    <Button
                                      size="small"
                                      variant="text"
                                      startIcon={<EditIcon fontSize="small" />}
                                      onClick={() => handleEditProductClick(product)}
                                    >
                                      <T>Editar</T>
                                    </Button>
                                  )}
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>

                  {(isOwner || storeData.mostrarEstadisticas !== false) && (
                  <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, backgroundColor: brandColors.backgroundPaper, borderColor: "divider" }}>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={1}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          <T>Calificación de la tienda</T>
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Rating value={storeData.avgRating || 0} precision={0.1} readOnly size="small" />
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {(storeData.avgRating || 0).toFixed(1)} (
                            {storeData.ratingCount} {storeData.ratingCount === 1 ? <T>calificación</T> : <T>calificaciones</T>})
                          </Typography>
                        </Stack>
                      </Stack>

                      {!isOwner && (
                        isGuest ? (
                          <Alert severity="info">
                            <T>Inicia sesión para calificar esta tienda.</T>
                          </Alert>
                        ) : (
                          <Stack spacing={1.5}>
                            <Rating
                              value={myRatingValue}
                              onChange={(event, newValue) => setMyRatingValue(newValue || 0)}
                            />
                            <TextField
                              label={<T>Comentario (opcional)</T>}
                              value={myRatingComment}
                              onChange={(e) => setMyRatingComment(e.target.value)}
                              multiline
                              minRows={2}
                            />
                            {ratingFeedback && (
                              <Alert severity={ratingFeedback.type} onClose={() => setRatingFeedback(null)}>
                                {ratingFeedback.message}
                              </Alert>
                            )}
                            <Box>
                              <Button variant="contained" onClick={handleSubmitRating} disabled={submittingRating}>
                                {submittingRating ? <T>Enviando...</T> : <T>Enviar calificación</T>}
                              </Button>
                            </Box>
                          </Stack>
                        )
                      )}

                      {ratings.length > 0 && (
                        <Stack spacing={1.5}>
                          <Divider sx={{ borderColor: "divider" }} />
                          {ratings.slice(0, 5).map((r) => (
                            <Box key={r.id} sx={{ display: "flex", gap: 1.5 }}>
                              <Avatar src={r.profileImageUrl} alt={r.displayName} sx={{ width: 32, height: 32 }} />
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      minWidth: 0,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {r.displayName}
                                  </Typography>
                                  <Rating value={r.rating} readOnly size="small" sx={{ flexShrink: 0 }} />
                                </Stack>
                                {r.comment && (
                                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                    {r.comment}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                  )}

                  <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, backgroundColor: brandColors.backgroundPaper, borderColor: "divider" }}>
                    <Box
                      onClick={() => setQaOpen((prev) => !prev)}
                      sx={{ display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none", minWidth: 0 }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, minWidth: 0, fontSize: { xs: "0.875rem", sm: "1.25rem" } }}>
                        <T>Preguntas y Respuestas</T> ({questionsAndAnswers.length})
                      </Typography>
                      <IconButton size="small" sx={{ ml: "auto", flexShrink: 0, color: accentColor }}>
                        {qaOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>

                    <Collapse in={qaOpen}>
                      <Stack spacing={2} sx={{ mt: 2 }}>
                        <List dense sx={{ p: 0 }}>
                          {questionsAndAnswers
                            .slice(0, showMoreQuestions ? undefined : 1)
                            .map((item) => (
                              <ListItem key={item.id} alignItems="flex-start" sx={{ p: 0, mb: 1 }}>
                                <Paper elevation={0} sx={{ p: 1.5, width: "100%", bgcolor: "action.hover", border: "1px solid", borderColor: "divider" }}>
                                  <Box display="flex" alignItems="center" mb={1} sx={{ minWidth: 0 }}>
                                    <Avatar src={item.profileImageUrl} alt={item.displayName} sx={{ width: 28, height: 28, mr: 1, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                                      <strong>{item.displayName}:</strong> {item.question}
                                    </Typography>
                                  </Box>
                                  {item.answers.map((answer) => (
                                    <Box key={answer.id} sx={{ ml: 4, mt: 0.5, mb: 1, minWidth: 0 }}>
                                      <Box display="flex" alignItems="center" sx={{ minWidth: 0 }}>
                                        <Avatar src={answer.profileImageUrl} alt={answer.displayName} sx={{ width: 24, height: 24, mr: 1, flexShrink: 0 }} />
                                        <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                                          <strong>{answer.displayName}:</strong> {answer.answer}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ))}
                                  {isOwner && !item.answers.length && (
                                    <Box sx={{ display: "flex", mt: 1, ml: 4 }}>
                                      <TextField
                                        fullWidth
                                        variant="standard"
                                        size="small"
                                        placeholder="Escribe tu respuesta..."
                                        value={newAnswer[item.id] || ""}
                                        onChange={(e) =>
                                          setNewAnswer((prev) => ({ ...prev, [item.id]: e.target.value }))
                                        }
                                        sx={{ "& .MuiInputBase-input": { padding: "8px 10px" } }}
                                      />
                                      <Button
                                        size="small"
                                        variant="contained"
                                        sx={{ ml: 1, textTransform: "none" }}
                                        onClick={() => handleAnswerSubmit(item.id)}
                                        disabled={!newAnswer[item.id]?.trim()}
                                      >
                                        <T>Responder</T>
                                      </Button>
                                    </Box>
                                  )}
                                </Paper>
                              </ListItem>
                            ))}
                        </List>

                        {questionsAndAnswers.length > 1 && (
                          <Button
                            size="small"
                            onClick={() => setShowMoreQuestions((prev) => !prev)}
                            sx={{ alignSelf: "flex-start", textTransform: "none", fontSize: "0.8rem" }}
                          >
                            {showMoreQuestions ? (
                              <T>Mostrar menos</T>
                            ) : (
                              <>
                                <T>Mostrar</T> {questionsAndAnswers.length - 1} <T>más</T>
                              </>
                            )}
                          </Button>
                        )}

                        {!isGuest ? (
                          <Box sx={{ display: "flex" }}>
                            <TextField
                              fullWidth
                              placeholder="Escribe una pregunta..."
                              variant="standard"
                              size="small"
                              value={newQuestion}
                              onChange={(e) => setNewQuestion(e.target.value)}
                              sx={{ "& .MuiInputBase-input": { padding: "8px 10px" } }}
                            />
                            <Button
                              sx={{ ml: 1, textTransform: "none" }}
                              variant="contained"
                              size="small"
                              onClick={handleQuestionSubmit}
                              disabled={!newQuestion.trim()}
                            >
                              <T>Preguntar</T>
                            </Button>
                          </Box>
                        ) : (
                          <Alert severity="info">
                            <T>Inicia sesión para hacer una pregunta.</T>
                          </Alert>
                        )}
                      </Stack>
                    </Collapse>
                  </Paper>
                </Stack>
              )}

              {showProductos && (
                <Stack spacing={2}>
                  {!editingProductId && pendingCommission.amount > 0 && (
                    <Alert
                      severity="warning"
                      action={
                        <Button size="small" color="inherit" onClick={() => setActiveTab("panel")}>
                          <T>Ir a pagar</T>
                        </Button>
                      }
                    >
                      <T>Tenés comisión pendiente — no vas a poder cargar productos nuevos hasta pagarla.</T>
                    </Alert>
                  )}
                  <Paper
                    ref={productFormRef}
                    variant="outlined"
                    component="form"
                    onSubmit={handleSubmitProduct}
                    sx={{ p: 3, borderRadius: 3, backgroundColor: brandColors.backgroundPaper, borderColor: "divider" }}
                  >
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={0.5}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {editingProductId ? <T>Editar producto</T> : <T>Agrega un nuevo producto</T>}
                        </Typography>
                        <Typography variant="caption" sx={{ color: products.length >= productLimit ? "error.main" : "text.secondary" }}>
                          <T>{products.length} / {productLimit === Infinity ? "∞" : productLimit} productos</T>
                        </Typography>
                      </Stack>
                      <Alert severity="info" sx={{ mb: 1 }}>
                        <T>¿Sabías que puedes definir un <b>código de cupón</b> para este producto? Compártelo con tus clientes para que apliquen un descuento directo al pagar.</T>
                      </Alert>
                      {!editingProductId && !canAddProduct && (
                        <Alert severity="warning" action={
                          <Button size="small" color="inherit" onClick={() => navigate("/subscription")}>
                            <T>Ver planes</T>
                          </Button>
                        }>
                          {productLimitMessage}
                        </Alert>
                      )}
                      <TextField
                        label={<T>Nombre</T>}
                        name="name"
                        value={productForm.name}
                        onChange={handleProductInputChange}
                        required
                      />
                      <TextField
                        label={<T>Precio en USD</T>}
                        name="price"
                        value={productForm.price}
                        onChange={handleProductInputChange}
                        required
                        type="number"
                        inputProps={{ min: 0, step: "0.01" }}
                        helperText={<T>Recibís el 100% de cada venta directo en tu cuenta de PayPal.</T>}
                      />
                      <TextField
                        label={<T>Cantidad en stock (opcional)</T>}
                        name="stock"
                        value={productForm.stock}
                        onChange={handleProductInputChange}
                        type="number"
                        inputProps={{ min: 0, step: "1" }}
                        helperText={<T>Dejalo vacío si no querés mostrar el stock disponible.</T>}
                      />
                      <TextField
                        label={<T>Descripción</T>}
                        name="description"
                        value={productForm.description}
                        onChange={handleProductInputChange}
                        multiline
                        minRows={3}
                        required
                      />
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                          label={<T>Código de cupón (opcional)</T>}
                          name="couponCode"
                          value={productForm.couponCode}
                          onChange={handleProductInputChange}
                          placeholder="DESCUENTO10"
                          fullWidth
                        />
                        <TextField
                          label={<T>Descuento (%)</T>}
                          name="couponDiscount"
                          value={productForm.couponDiscount}
                          onChange={handleProductInputChange}
                          type="number"
                          inputProps={{ min: 1, max: 90, step: "1" }}
                          disabled={!productForm.couponCode.trim()}
                          required={Boolean(productForm.couponCode.trim())}
                          fullWidth
                        />
                      </Stack>
                      <Stack spacing={1.5}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<AddPhotoAlternateIcon />}
                            disabled={existingImages.length + productImages.length >= MAX_PRODUCT_IMAGES}
                          >
                            <T>Agregar imágenes</T>
                            <Input
                              key={imageInputKey}
                              type="file"
                              accept="image/*"
                              inputProps={{ multiple: true }}
                              onChange={handleProductImageChange}
                              sx={{ display: "none" }}
                            />
                          </Button>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            <T>{existingImages.length + productImages.length} / {MAX_PRODUCT_IMAGES} imágenes</T>
                          </Typography>
                        </Stack>

                        {(existingImages.length > 0 || productImages.length > 0) && (
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {existingImages.map((url, i) => (
                              <Box key={`existing-${url}`} sx={{ position: "relative", width: 72, height: 72, borderRadius: 1.5, overflow: "hidden" }}>
                                <Box component="img" src={url} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveExistingImage(i)}
                                  sx={{ position: "absolute", top: 2, right: 2, bgcolor: "rgba(0,0,0,0.55)", color: "white", p: 0.3, "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
                                >
                                  <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            ))}
                            {productImagePreviews.map((url, i) => (
                              <Box key={`new-${url}`} sx={{ position: "relative", width: 72, height: 72, borderRadius: 1.5, overflow: "hidden" }}>
                                <Box component="img" src={url} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveNewImage(i)}
                                  sx={{ position: "absolute", top: 2, right: 2, bgcolor: "rgba(0,0,0,0.55)", color: "white", p: 0.3, "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
                                >
                                  <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        )}

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                          <Button variant="outlined" component="label" startIcon={<VideoCallIcon />}>
                            {productVideo ? <T>Cambiar video</T> : <T>Subir video (opcional)</T>}
                            <Input
                              key={videoInputKey}
                              type="file"
                              accept="video/*"
                              onChange={handleProductVideoChange}
                              sx={{ display: "none" }}
                            />
                          </Button>
                          {productVideo && (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <PlayCircleIcon fontSize="small" sx={{ color: "text.secondary" }} />
                              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {productVideo.name}
                              </Typography>
                              <IconButton size="small" onClick={handleRemoveVideo}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          )}
                          {!productVideo && editingProductId && existingVideoUrl && !removeExistingVideo && (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                <T>Se conservará el video actual.</T>
                              </Typography>
                              <Button size="small" color="error" onClick={handleRemoveVideo}>
                                <T>Quitar</T>
                              </Button>
                            </Stack>
                          )}
                        </Stack>
                      </Stack>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={savingProduct || (!editingProductId && (!canAddProduct || pendingCommission.amount > 0))}
                        >
                          {savingProduct
                            ? <T>Guardando...</T>
                            : editingProductId
                            ? <T>Actualizar producto</T>
                            : <T>Guardar producto</T>}
                        </Button>
                        <Button variant="text" onClick={resetProductForm} disabled={savingProduct}>
                          {editingProductId ? <T>Cancelar edición</T> : <T>Limpiar</T>}
                        </Button>
                      </Box>
                    </Stack>
                  </Paper>
                </Stack>
              )}

              {showPanel && <StoreDashboardTab storeSlug={storeSlug} storeData={storeData} onCommissionPaid={refreshPendingCommission} />}

              {showApariencia && (
                <StoreAppearanceForm
                  storeSlug={storeSlug}
                  storeData={storeData}
                  onSaved={handleAppearanceSaved}
                />
              )}
            </Stack>
          )}
        </Paper>
        {!loading && storeLoaded && !error && showTienda && <GoogleAdInline />}
      </Container>
    </Box>
  );
};

export default StoreDetail;
